// Typed activity builder forms for the Content CMS lesson editor.
// One form per activity `type`; the shape mirrors the Zod payload schemas in
// packages/zod-schemas/content.ts. Every text field is a translation KEY (authors
// fill the strings in the Translations panel), `media` fields are relative R2
// keys. The API re-validates whatever shape we send, so this is a convenience
// layer — the array-heavy types (options/pairs/blanks) fall back to a guided
// JSON editor rather than a bespoke nested-array UI.
//
// Filename starts with `-` so TanStack Router treats it as a non-route module.

import { Input, Textarea } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { MediaThumb, UploadButton } from './_authed.media';

import type { ActivityType } from '~/lib/content';
import { type MediaAsset, type MediaKind, listMedia } from '~/lib/media';

// ─── Minimal valid default payloads (seed on "add activity") ────────────────

export function defaultDataFor(type: ActivityType): Record<string, unknown> {
  switch (type) {
    case 'intro_video':
      return { video: 'lessons/placeholder/intro.mp4', titleKey: 'content.placeholder.title' };
    case 'letter_card':
      return {
        glyph: 'ا',
        nameKey: 'content.placeholder.title',
        soundKey: 'content.placeholder.body',
        exampleWordKey: 'content.placeholder.word',
      };
    case 'vocabulary_card':
      return { wordKey: 'content.placeholder.word', glossKey: 'content.placeholder.gloss' };
    case 'tap_match':
      return {
        instructionKey: 'content.placeholder.instruction',
        pairs: [
          { id: 'a', promptKey: 'content.placeholder.a', matchKey: 'content.placeholder.a_en' },
          { id: 'b', promptKey: 'content.placeholder.b', matchKey: 'content.placeholder.b_en' },
        ],
      };
    case 'drag_sort':
      return {
        instructionKey: 'content.placeholder.instruction',
        categories: [
          { id: 'x', labelKey: 'content.placeholder.cat_x' },
          { id: 'y', labelKey: 'content.placeholder.cat_y' },
        ],
        items: [
          { id: 'i1', labelKey: 'content.placeholder.i1', categoryId: 'x' },
          { id: 'i2', labelKey: 'content.placeholder.i2', categoryId: 'y' },
        ],
      };
    case 'multiple_choice':
      return {
        promptKey: 'content.placeholder.prompt',
        options: [
          { id: 'a', labelKey: 'content.placeholder.a' },
          { id: 'b', labelKey: 'content.placeholder.b' },
        ],
        correctId: 'a',
      };
    case 'fill_blank':
      return {
        instructionKey: 'content.placeholder.instruction',
        segments: [
          { kind: 'text', textKey: 'content.placeholder.sentence' },
          { kind: 'blank', blankId: 'b1' },
        ],
        blanks: [{ id: 'b1', accepted: ['answer'] }],
      };
    case 'listen_choose':
      return {
        audio: 'lessons/placeholder/clip.mp3',
        instructionKey: 'content.placeholder.instruction',
        options: [
          { id: 'a', labelKey: 'content.placeholder.a' },
          { id: 'b', labelKey: 'content.placeholder.b' },
        ],
        correctId: 'a',
      };
    case 'record_repeat':
      return { phraseKey: 'content.placeholder.phrase', glossKey: 'content.placeholder.gloss' };
    case 'letter_trace':
      return {
        glyph: 'ا',
        nameKey: 'content.placeholder.title',
        viewBox: '0 0 100 100',
        strokes: ['M 50 15 L 50 85'],
      };
    case 'read_aloud_passage':
      return {
        titleKey: 'content.placeholder.title',
        bodyKey: 'content.placeholder.body',
        allowTashkeelToggle: true,
      };
    case 'story_scene':
      return { bodyKey: 'content.placeholder.body' };
    case 'quiz_summary':
      return {
        titleKey: 'content.common.quiz_title',
        questions: [
          {
            id: 'q1',
            promptKey: 'content.placeholder.prompt',
            options: [
              { id: 'a', labelKey: 'content.placeholder.a' },
              { id: 'b', labelKey: 'content.placeholder.b' },
            ],
            correctId: 'a',
          },
        ],
      };
    case 'printable_homework':
      return {
        titleKey: 'content.placeholder.title',
        pdf: 'printables/placeholder/worksheet.pdf',
      };
  }
}

// Types that get a bespoke typed form. The rest (array-heavy) use the guided
// JSON editor — still validated by the API, with a schema hint shown.
const TYPED_FORM_TYPES = new Set<ActivityType>([
  'intro_video',
  'letter_card',
  'vocabulary_card',
  'multiple_choice',
  'listen_choose',
  'record_repeat',
  'read_aloud_passage',
  'story_scene',
  'printable_homework',
]);

export function hasTypedForm(type: ActivityType): boolean {
  return TYPED_FORM_TYPES.has(type);
}

// ─── Small field primitives ─────────────────────────────────────────────────

function KeyField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label className="block text-sm font-medium text-ink-700">
      {label}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="lesson.x.key"
        className="mt-1 font-mono text-xs"
      />
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

function MediaField({
  label,
  value,
  onChange,
  kind,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  kind?: MediaKind;
}) {
  const [picking, setPicking] = useState(false);
  return (
    <div className="block text-sm font-medium text-ink-700">
      {label}
      <div className="mt-1 flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="uploads/<kind>/<id>.ext"
          className="flex-1 font-mono text-xs"
        />
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="shrink-0 rounded-2xl bg-white px-3 py-2 text-xs font-semibold text-burgundy-600 ring-1 ring-burgundy-500/20 hover:bg-cream-100"
        >
          Browse
        </button>
      </div>
      <span className="mt-1 block text-xs text-ink-500">
        R2 key — pick from the library or type one. Leave blank for none.
      </span>
      {picking && (
        <MediaPicker
          kind={kind}
          onPick={(r2Key) => {
            onChange(r2Key);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

// A modal that browses the media library (optionally filtered to a kind) and
// returns the selected asset's r2Key. Uploading here adds to the library and
// selects the new asset.
function MediaPicker({
  kind,
  onPick,
  onClose,
}: {
  kind?: MediaKind;
  onPick: (r2Key: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const query = useQuery({
    queryKey: ['media', kind ?? 'all', q, 'picker'],
    queryFn: ({ signal }) => listMedia({ kind, q: q || undefined }, signal),
    staleTime: 2_000,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl bg-cream-50 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Pick media"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-ink-900">Pick {kind ?? 'media'}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 place-items-center rounded-full text-ink-500 hover:bg-cream-200"
          >
            ✕
          </button>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by key…"
            className="flex-1"
          />
          <UploadButton defaultKind={kind} compact onUploaded={(a) => onPick(a.r2Key)} />
        </div>

        <div className="mt-4 grid flex-1 grid-cols-3 gap-3 overflow-auto sm:grid-cols-4">
          {query.isLoading ? (
            <p className="col-span-full text-sm text-ink-500">Loading…</p>
          ) : query.data && query.data.length > 0 ? (
            query.data.map((m: MediaAsset) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onPick(m.r2Key)}
                title={m.r2Key}
                className="overflow-hidden rounded-xl border border-burgundy-500/10 bg-white text-start transition-colors hover:border-sun-300"
              >
                <span className="grid aspect-square place-items-center bg-cream-100/50">
                  <MediaThumb asset={m} />
                </span>
                <span className="block truncate p-2 font-mono text-[10px] text-ink-600">
                  {m.r2Key.split('/').pop()}
                </span>
              </button>
            ))
          ) : (
            <p className="col-span-full text-sm text-ink-500">No media yet — upload one above.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── The dispatcher ─────────────────────────────────────────────────────────
//
// `data` is the current payload; `onChange` receives the full next payload.
// Typed forms edit known fields directly; unknown fields are preserved by
// spreading `data`. The JSON fallback owns the whole payload.

export interface ActivityFormProps {
  type: ActivityType;
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

export function ActivityForm({ type, data, onChange }: ActivityFormProps) {
  if (!hasTypedForm(type)) return <JsonForm data={data} onChange={onChange} type={type} />;

  const str = (k: string): string => (typeof data[k] === 'string' ? data[k] : '');
  const set = (k: string, v: string): void => onChange({ ...data, [k]: v || undefined });

  switch (type) {
    case 'intro_video':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <MediaField
            kind="video"
            label="Video"
            value={str('video')}
            onChange={(v) => set('video', v)}
          />
          <MediaField
            label="Poster image"
            value={str('poster')}
            onChange={(v) => set('poster', v)}
          />
          <MediaField
            label="Captions (.vtt)"
            value={str('captions')}
            onChange={(v) => set('captions', v)}
          />
          <KeyField
            label="Title key"
            value={str('titleKey')}
            onChange={(v) => set('titleKey', v)}
          />
        </div>
      );
    case 'letter_card':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block text-sm font-medium text-ink-700">
            Glyph (with tashkeel)
            <Input
              value={str('glyph')}
              onChange={(e) => set('glyph', e.target.value)}
              className="mt-1 text-lg"
            />
          </label>
          <KeyField label="Name key" value={str('nameKey')} onChange={(v) => set('nameKey', v)} />
          <KeyField
            label="Sound key"
            value={str('soundKey')}
            onChange={(v) => set('soundKey', v)}
          />
          <KeyField
            label="Example word key"
            value={str('exampleWordKey')}
            onChange={(v) => set('exampleWordKey', v)}
          />
          <KeyField
            label="Example gloss key"
            value={str('exampleGlossKey')}
            onChange={(v) => set('exampleGlossKey', v)}
          />
          <MediaField
            label="Audio: name"
            value={str('audioName')}
            onChange={(v) => set('audioName', v)}
          />
          <MediaField
            label="Audio: example"
            value={str('audioExample')}
            onChange={(v) => set('audioExample', v)}
          />
          <MediaField
            label="Image: example"
            value={str('imageExample')}
            onChange={(v) => set('imageExample', v)}
          />
        </div>
      );
    case 'vocabulary_card':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <KeyField
            label="Word key (Arabic)"
            value={str('wordKey')}
            onChange={(v) => set('wordKey', v)}
          />
          <KeyField
            label="Gloss key (English)"
            value={str('glossKey')}
            onChange={(v) => set('glossKey', v)}
          />
          <MediaField
            kind="image"
            label="Image"
            value={str('image')}
            onChange={(v) => set('image', v)}
          />
          <MediaField
            kind="audio"
            label="Audio"
            value={str('audio')}
            onChange={(v) => set('audio', v)}
          />
        </div>
      );
    case 'multiple_choice':
      return (
        <div className="grid gap-4">
          <KeyField
            label="Prompt key"
            value={str('promptKey')}
            onChange={(v) => set('promptKey', v)}
          />
          <MediaField
            label="Prompt audio"
            value={str('promptAudio')}
            onChange={(v) => set('promptAudio', v)}
          />
          <OptionsEditor
            data={data}
            onChange={onChange}
            hint="2–4 options; the correct one is picked below."
          />
        </div>
      );
    case 'listen_choose':
      return (
        <div className="grid gap-4">
          <MediaField
            label="Audio (required)"
            value={str('audio')}
            onChange={(v) => set('audio', v)}
          />
          <KeyField
            label="Instruction key"
            value={str('instructionKey')}
            onChange={(v) => set('instructionKey', v)}
          />
          <OptionsEditor
            data={data}
            onChange={onChange}
            hint="2–4 options; pick the correct one."
          />
        </div>
      );
    case 'record_repeat':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <KeyField
            label="Phrase key"
            value={str('phraseKey')}
            onChange={(v) => set('phraseKey', v)}
          />
          <KeyField
            label="Gloss key"
            value={str('glossKey')}
            onChange={(v) => set('glossKey', v)}
          />
          <MediaField
            label="Model audio"
            value={str('modelAudio')}
            onChange={(v) => set('modelAudio', v)}
          />
        </div>
      );
    case 'read_aloud_passage':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <KeyField
            label="Title key"
            value={str('titleKey')}
            onChange={(v) => set('titleKey', v)}
          />
          <KeyField
            label="Body key (passage)"
            value={str('bodyKey')}
            onChange={(v) => set('bodyKey', v)}
          />
          <MediaField
            label="Narration audio"
            value={str('audio')}
            onChange={(v) => set('audio', v)}
          />
          <label className="flex items-center gap-2 self-end text-sm font-medium text-ink-700">
            <input
              type="checkbox"
              checked={data.allowTashkeelToggle !== false}
              onChange={(e) => onChange({ ...data, allowTashkeelToggle: e.target.checked })}
            />
            Show tashkeel toggle
          </label>
        </div>
      );
    case 'story_scene':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <KeyField
            label="Body key (paragraph)"
            value={str('bodyKey')}
            onChange={(v) => set('bodyKey', v)}
          />
          <MediaField
            kind="image"
            label="Illustration"
            value={str('image')}
            onChange={(v) => set('image', v)}
          />
          <MediaField
            label="Narration audio"
            value={str('audio')}
            onChange={(v) => set('audio', v)}
          />
          <p className="self-end text-xs text-ink-500">
            Tap-to-reveal vocab: edit as JSON via “Advanced” if needed.
          </p>
        </div>
      );
    case 'printable_homework':
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <KeyField
            label="Title key"
            value={str('titleKey')}
            onChange={(v) => set('titleKey', v)}
          />
          <KeyField
            label="Description key"
            value={str('descriptionKey')}
            onChange={(v) => set('descriptionKey', v)}
          />
          <MediaField
            kind="pdf"
            label="PDF (required)"
            value={str('pdf')}
            onChange={(v) => set('pdf', v)}
          />
        </div>
      );
    default:
      return <JsonForm data={data} onChange={onChange} type={type} />;
  }
}

// ─── Options editor (multiple_choice / listen_choose) ───────────────────────

interface Option {
  id: string;
  labelKey: string;
  image?: string | null;
}

function OptionsEditor({
  data,
  onChange,
  hint,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  hint?: string;
}) {
  const options: Option[] = Array.isArray(data.options) ? (data.options as Option[]) : [];
  const correctId = typeof data.correctId === 'string' ? data.correctId : '';

  function setOptions(next: Option[], nextCorrect?: string): void {
    onChange({
      ...data,
      options: next,
      correctId: nextCorrect ?? (next.some((o) => o.id === correctId) ? correctId : next[0]?.id),
    });
  }

  return (
    <div className="rounded-2xl border border-burgundy-500/10 bg-cream-100/30 p-4">
      <p className="text-sm font-semibold text-ink-900">Options</p>
      {hint && <p className="mt-0.5 text-xs text-ink-500">{hint}</p>}
      <div className="mt-3 space-y-2">
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="radio"
              name="correct"
              checked={correctId === opt.id}
              onChange={() => setOptions(options, opt.id)}
              aria-label="Correct answer"
            />
            <Input
              value={opt.id}
              onChange={(e) => {
                const next = options.slice();
                const oldId = next[i]!.id;
                next[i] = { ...next[i]!, id: e.target.value };
                setOptions(next, correctId === oldId ? e.target.value : correctId);
              }}
              placeholder="id"
              className="w-24 font-mono text-xs"
            />
            <Input
              value={opt.labelKey}
              onChange={(e) => {
                const next = options.slice();
                next[i] = { ...next[i]!, labelKey: e.target.value };
                setOptions(next);
              }}
              placeholder="label key"
              className="flex-1 font-mono text-xs"
            />
            <button
              type="button"
              onClick={() => setOptions(options.filter((_, j) => j !== i))}
              disabled={options.length <= 2}
              className="rounded-full px-2 py-1 text-xs text-heart hover:bg-heart/10 disabled:opacity-30"
              aria-label="Remove option"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => {
          if (options.length >= 4) return;
          const id = String.fromCharCode(97 + options.length);
          setOptions([...options, { id, labelKey: 'content.placeholder.option' }]);
        }}
        disabled={options.length >= 4}
        className="mt-3 rounded-full bg-white px-3 py-1 text-xs font-semibold text-burgundy-500 ring-1 ring-burgundy-500/20 hover:bg-cream-100 disabled:opacity-40"
      >
        + Add option
      </button>
    </div>
  );
}

// ─── JSON fallback (tap_match / drag_sort / fill_blank / quiz_summary) ──────

function JsonForm({
  data,
  onChange,
  type,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  type: ActivityType;
}) {
  const [json, setJson] = useState(() => JSON.stringify(data, null, 2));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setJson(JSON.stringify(data, null, 2));
  }, [data]);

  return (
    <div>
      <p className="mb-2 text-xs text-ink-500">
        <span className="font-mono">{type}</span> — edit the payload directly. Every text field is a
        translation key; the server validates the shape on save.
      </p>
      <Textarea
        value={json}
        onChange={(e) => {
          setJson(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value) as Record<string, unknown>;
            setError(null);
            onChange(parsed);
          } catch {
            setError('Invalid JSON — fix to save.');
          }
        }}
        className="font-mono text-xs leading-relaxed"
        rows={Math.min(20, Math.max(8, json.split('\n').length))}
      />
      {error && <p className="mt-1 text-xs text-heart">{error}</p>}
    </div>
  );
}
